// src/sectors/pilates/staff/modal/staffLoginModal/ConfirmLoginStatusModal.jsx
import React from 'react';

// MUI
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';

// MD2 components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

export function ConfirmLoginStatusModal({
  open,
  staff,
  currentStatus, // "ACTIVE" | "SUSPENDED"
  loading,
  onClose,
  onConfirm, // (nextStatus) => Promise
}) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!open) return null;

  const isSuspended = currentStatus === 'SUSPENDED';
  const nextStatus = isSuspended ? 'ACTIVE' : 'SUSPENDED';

  const title = isSuspended ? 'Girişi Aç' : 'Girişi Kapat';
  const desc = isSuspended
    ? 'Bu eğitmen tekrar giriş yapabilecek. Emin misin?'
    : 'Artık bu eğitmen giriş yapamayacak. Emin misin?';

  const toneBg = isSuspended
    ? 'linear-gradient(180deg, rgba(46,204,113,.16), rgba(46,204,113,.06))'
    : 'linear-gradient(180deg, rgba(255,72,72,.16), rgba(255,72,72,.06))';

  const iconName = isSuspended ? 'check_circle' : 'lock';

  const handleConfirm = async () => {
    await onConfirm?.(nextStatus);
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '20px',
          border: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          boxShadow: theme.shadows[12],
        },
      }}
    >
      <DialogTitle sx={{ p: 0, background: toneBg }}>
        <MDBox sx={{ px: 2, py: 1.4 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <MDBox
              sx={{
                width: 40,
                height: 40,
                borderRadius: '14px',
                display: 'grid',
                placeItems: 'center',
                background: isSuspended ? 'rgba(46,204,113,.14)' : 'rgba(255,72,72,.14)',
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Icon sx={{ fontSize: 20 }}>{iconName}</Icon>
            </MDBox>

            <MDBox>
              <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                {title}
              </MDTypography>
              <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                {staff?.fullName || 'Seçili eğitmen'}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2 }}>
        <MDTypography variant="button" sx={{ color: theme.palette.text.primary, opacity: 0.95 }}>
          {desc}
        </MDTypography>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <MDButton
          variant="outlined"
          color="dark"
          sx={{ borderRadius: '14px' }}
          onClick={onClose}
          disabled={loading}
        >
          Vazgeç
        </MDButton>

        <MDButton
          variant="gradient"
          color={isSuspended ? 'success' : 'error'}
          sx={{ borderRadius: '14px' }}
          onClick={handleConfirm}
          disabled={loading}
        >
          <Icon sx={{ mr: 0.5, fontSize: 18 }}>{iconName}</Icon>
          {isSuspended ? 'Evet, Aç' : 'Evet, Kapat'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
